import { useRef, useMemo, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { usePlayer } from '../../../context/PlayerContext'
import { useDiscogsConnection } from '../../../context/DiscogsContext'
import { useDiscogsMembership } from '../../../hooks/useDiscogsLists'
import { useDiscogsWriteGate } from '../../DiscogsWriteGate'
import axiosClient from '../../../api/client'
import { useToast } from '../../../context/ToastContext'
import { useKeyboardShortcuts } from '../../../hooks/useKeyboardShortcuts'
import { YouTubePlayer, YouTubePlayerRef } from '../YouTubePlayer'
import { QueuePanel } from '../QueuePanel'
import { cn } from '../../../utils'
import styles from './MusicPlayer.module.css'

function formatTime(seconds: number): string {
  if (!seconds || isNaN(seconds)) return '0:00'
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

export function MusicPlayer() {
  const {
    isPlaying,
    currentTrack,
    queue,
    volume,
    progress,
    duration,
    isQueueOpen,
    isHistoryOpen,
    repeatMode,
    shuffle,
    autoSkip30,
    radioSeedId,
    togglePlayPause,
    next,
    previous,
    seekTo,
    setVolume,
    toggleQueue,
    toggleHistory,
    toggleRepeat,
    toggleShuffle,
    toggleAutoSkip30,
    toggleRadio,
  } = usePlayer()

  const { isAuthenticated } = useDiscogsConnection()
  const { isInWantlist, addToWantlist, removeFromWantlist } = useDiscogsMembership()
  const { gate: discogsGate } = useDiscogsWriteGate()
  const { showToast } = useToast()

  const playerRef = useRef<YouTubePlayerRef>(null)
  const progressBarRef = useRef<HTMLDivElement>(null)

  // Setup keyboard shortcuts - memoized to prevent recreation on every render
  const shortcuts = useMemo(
    () => [
      {
        key: ' ',
        handler: togglePlayPause,
        description: 'Play/Pause',
      },
      {
        key: 'ArrowLeft',
        handler: previous,
        description: 'Previous track',
      },
      {
        key: 'ArrowRight',
        handler: next,
        description: 'Next track',
      },
    ],
    [togglePlayPause, previous, next]
  )

  useKeyboardShortcuts(shortcuts, queue.length > 0)

  const handleCopyLink = () => {
    if (!currentTrack?.videoUrl) return
    navigator.clipboard.writeText(currentTrack.videoUrl)
      .then(() => showToast('YouTube link copied', 'success'))
      .catch(() => showToast('Failed to copy link', 'error'))
  }

  const handleAddToWantlist = async () => {
    if (!currentTrack || !isAuthenticated) {
      showToast('Please connect your Discogs account in Settings first', 'warning')
      return
    }

    const inWantlist = isInWantlist(currentTrack.releaseId)
    const action = inWantlist ? 'remove-wantlist' : 'add-wantlist'
    const releaseLabel = `${currentTrack.artist ?? ''}${currentTrack.artist ? ' — ' : ''}${currentTrack.releaseTitle ?? ''}`.trim() || undefined
    const ok = await discogsGate(action, { releaseLabel })
    if (!ok) return

    try {
      if (inWantlist) {
        await axiosClient.delete(`/discogs/wantlist/${currentTrack.releaseId}`)
        removeFromWantlist(currentTrack.releaseId)
        showToast('Removed from Discogs wantlist', 'success')
      } else {
        await axiosClient.post('/discogs/wantlist', { releaseId: currentTrack.releaseId })
        addToWantlist(currentTrack.releaseId)
        showToast('Added to Discogs wantlist', 'success')
      }
    } catch (error: any) {
      showToast(error?.response?.data?.message || 'Failed to update wantlist', 'error')
    }
  }

  // Reserve bottom space for centered states while the player is visible
  const playerVisible = queue.length > 0
  useEffect(() => {
    document.documentElement.classList.toggle('player-active', playerVisible)
    return () => document.documentElement.classList.remove('player-active')
  }, [playerVisible])

  // Handle progress bar click
  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressBarRef.current || !duration) return
    const rect = progressBarRef.current.getBoundingClientRect()
    const percent = (e.clientX - rect.left) / rect.width
    const newTime = percent * duration
    seekTo(newTime)
  }

  // Don't render if queue is empty
  if (queue.length === 0) return null

  const progressPercent = duration > 0
    ? Math.max(0, Math.min(100, (progress / duration) * 100))
    : 0

  return (
    <>
      <QueuePanel />

      {/* Hidden YouTube Player (audio only) */}
      <div className={styles.hiddenPlayer}>
        <YouTubePlayer ref={playerRef} />
      </div>

      {/* Player Bar */}
      <div className={styles.playerBar}>
        {/* Progress Bar (top of player) */}
        <div
          ref={progressBarRef}
          className={styles.progressBar}
          onClick={handleProgressClick}
        >
          <div
            key={currentTrack?.id ?? 'empty'}
            className={styles.progressFill}
            style={{ width: `${progressPercent}%` }}
          />
        </div>

        <div className={styles.playerContent}>
          {/* Track Info */}
          <div className={styles.trackInfo}>
            {currentTrack?.thumbnail ? (
              <img
                src={currentTrack.thumbnail}
                alt={currentTrack.releaseTitle}
                className={styles.thumbnail}
              />
            ) : (
              <div className={styles.noThumbnail}>
                <i className="bi bi-music-note-beamed"></i>
              </div>
            )}
            <div className={styles.trackDetails}>
              <div className={styles.trackTitle}>
                <Link
                  to={`/releases/${currentTrack?.releaseId}`}
                  className={styles.trackLink}
                >
                  {currentTrack?.title || 'No track'}
                </Link>
              </div>
              <div className={styles.trackArtist}>
                {currentTrack?.artistId ? (
                  <Link
                    to={`/artists?artist_id=${currentTrack.artistId}&artist_name=${encodeURIComponent(currentTrack.artist)}`}
                    className={styles.trackLink}
                  >
                    {currentTrack.artist}
                  </Link>
                ) : currentTrack?.artist}
                {' – '}
                <Link
                  to={`/releases/${currentTrack?.releaseId}`}
                  className={styles.trackLink}
                >
                  {currentTrack?.releaseTitle}
                </Link>
                {currentTrack?.labelName && (
                  <>
                    {' · '}
                    <Link
                      to={`/labels?label=${encodeURIComponent(currentTrack.labelName)}`}
                      className={styles.trackLink}
                    >
                      {currentTrack.labelName}
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Playback Controls */}
          <div className={styles.controls}>
            <button
              className={cn(styles.controlBtn, styles.secondary, shuffle && styles.active)}
              onClick={toggleShuffle}
              title="Shuffle"
            >
              <i className="bi bi-shuffle"></i>
            </button>

            <button
              className={cn(styles.controlBtn, styles.secondary)}
              onClick={previous}
              title="Previous"
            >
              <i className="bi bi-skip-start-fill"></i>
            </button>

            <button
              className={cn(styles.controlBtn, styles.primary)}
              onClick={() => {
                // Call playVideo/pauseVideo synchronously within the tap gesture
                // so iOS Safari honours it without waiting for a useEffect cycle
                if (isPlaying) {
                  playerRef.current?.pause()
                } else {
                  playerRef.current?.play()
                }
                togglePlayPause()
              }}
              title={isPlaying ? 'Pause' : 'Play'}
            >
              <i className={`bi ${isPlaying ? 'bi-pause-fill' : 'bi-play-fill'}`}></i>
            </button>

            <button
              className={cn(styles.controlBtn, styles.secondary)}
              onClick={next}
              title="Next"
            >
              <i className="bi bi-skip-end-fill"></i>
            </button>

            <button
              className={cn(styles.controlBtn, styles.secondary, repeatMode !== 'none' && styles.active)}
              onClick={toggleRepeat}
              title={`Repeat: ${repeatMode}`}
            >
              <i className={`bi ${repeatMode === 'one' ? 'bi-repeat-1' : 'bi-repeat'}`}></i>
            </button>

            <button
              className={cn(styles.controlBtn, styles.secondary, radioSeedId != null && styles.active)}
              onClick={toggleRadio}
              disabled={!currentTrack}
              title={radioSeedId != null ? 'Radio on — auto-queuing similar records' : 'Start radio from this track'}
              aria-pressed={radioSeedId != null}
            >
              <i className="bi bi-broadcast"></i>
            </button>
          </div>

          {/* Time & Volume */}
          <div className={styles.rightControls}>
            <span className={styles.time}>
              {formatTime(progress)} / {formatTime(duration)}
            </span>

            <div className={styles.volumeControl}>
              <button
                className={styles.volumeBtn}
                onClick={() => setVolume(volume > 0 ? 0 : 80)}
                title={volume > 0 ? 'Mute' : 'Unmute'}
              >
                <i className={`bi ${volume === 0 ? 'bi-volume-mute' : volume < 50 ? 'bi-volume-down' : 'bi-volume-up'}`}></i>
              </button>
              <input
                type="range"
                min="0"
                max="100"
                value={volume}
                onChange={(e) => setVolume(parseInt(e.target.value))}
                className={styles.volumeSlider}
              />
            </div>

            <button
              className={cn(styles.controlBtn, styles.skipBtn, autoSkip30 && styles.active)}
              onClick={toggleAutoSkip30}
              title={autoSkip30 ? 'Auto-skip 30%: ON' : 'Auto-skip 30%: OFF'}
            >
              <i className="bi bi-fast-forward"></i>
              <span className={styles.skipLabel}>30%</span>
            </button>

            {currentTrack && (
              <button
                className={cn(styles.controlBtn)}
                onClick={handleCopyLink}
                title="Copy YouTube link"
              >
                <i className="bi bi-link-45deg"></i>
              </button>
            )}

            {currentTrack && (
              <button
                className={cn(
                  styles.controlBtn,
                  styles.wantlistBtn,
                  isInWantlist(currentTrack.releaseId) && styles.active
                )}
                onClick={handleAddToWantlist}
                disabled={!isAuthenticated}
                title={isAuthenticated ? (isInWantlist(currentTrack.releaseId) ? 'Remove from wantlist' : 'Add to wantlist') : 'Login to add to wantlist'}
              >
                <i className={`bi ${isInWantlist(currentTrack.releaseId) ? 'bi-heart-fill' : 'bi-heart'}`}></i>
              </button>
            )}

            <button
              className={cn(styles.controlBtn, styles.historyBtn, isHistoryOpen && styles.active)}
              onClick={toggleHistory}
              title="History"
            >
              <i className="bi bi-clock-history"></i>
            </button>

            <button
              className={cn(styles.controlBtn, styles.queueBtn, isQueueOpen && styles.active)}
              onClick={toggleQueue}
              title="Queue"
            >
              <i className="bi bi-music-note-list"></i>
              {queue.length > 0 && (
                <span className={styles.queueCount}>{queue.length}</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
